import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { usePolling } from '../hooks/useApi';
import { truncateDecimalAmount } from '../utils/numberFormat';
import { ManualTradeRecovery } from '../components/ManualTradeRecovery';

const TOKENS = [
  { value: 'TON', label: 'TON / GRAM' },
  { value: 'USDT', label: 'USDT' },
  { value: 'EVAA', label: 'EVAA' },
  { value: 'DOGS', label: 'DOGS' },
  { value: 'UTYA', label: 'UTYA' },
  { value: 'NOT', label: 'NOT (Notcoin)' },
  { value: 'STORM', label: 'STORM' },
  { value: 'REDO', label: 'REDO' },
];

interface ManagedWalletStatus {
  configured: boolean;
  address: string;
  rawAddress: string;
  version: string;
  addressMatches: boolean;
  executionEnabled: boolean;
  killSwitch: boolean;
  canExecute: boolean;
  settlementBlocked?: boolean;
  maxAttachedTon: string;
  issue: string | null;
}

interface PreparedSwap {
  swapId: string;
  provider: string;
  providerLabel: string;
  tokenIn: string;
  tokenOut: string;
  amountIn: string;
  amountOut: string;
  minAmountOut: string;
  gasBudget: string | null;
  route: string[];
  walletAddress: string;
  validUntil: number;
  preparedAt: string;
  messageCount: number;
}

function routeText(quote: any): string {
  if (!quote?.route?.length) return 'маршрут не раскрыт';
  const short = quote.route.slice(0, 3).join(' -> ');
  return quote.route.length > 3 ? short + ' -> ...' : short;
}

function shortAddress(address: string): string {
  return address ? address.slice(0, 6) + '...' + address.slice(-6) : '';
}

function nanoTon(raw: string | null): string {
  if (!raw) return '';
  const value = Number(raw) / 1e9;
  return Number.isFinite(value) ? truncateDecimalAmount(value, 2) : raw;
}

function formatSwapAmount(value: string | number | null | undefined): string {
  return truncateDecimalAmount(value, 2);
}

export function RouterPage() {
  const { data: walletStatus, refetch: refetchWalletStatus } = usePolling(
    () => api.getDexWalletStatus() as Promise<ManagedWalletStatus>,
    5000
  );
  const { data: limitOrders, refetch: refetchLimitOrders } = usePolling(() => api.getDexLimitOrders(), 2000);
  const [tokenIn, setTokenIn] = useState('USDT');
  const [tokenOut, setTokenOut] = useState('EVAA');
  const [provider, setProvider] = useState('best');
  const [amount, setAmount] = useState('100');
  const [slippage, setSlippage] = useState('1.0');
  const [splits, setSplits] = useState('2');
  const [pathLength, setPathLength] = useState('3');
  const [quoteResult, setQuoteResult] = useState<any | null>(null);
  const [preparedSwap, setPreparedSwap] = useState<PreparedSwap | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [prepareLoading, setPrepareLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [limitToken, setLimitToken] = useState('EVAA');
  const [limitAmountUsdt, setLimitAmountUsdt] = useState('100');
  const [limitPriceUsdt, setLimitPriceUsdt] = useState('');
  const [limitSlippage, setLimitSlippage] = useState('1.0');
  const [limitProvider, setLimitProvider] = useState('best');
  const [limitProtectionMode, setLimitProtectionMode] = useState('recover_retry');
  const [limitMaxAttempts, setLimitMaxAttempts] = useState('3');
  const [limitRetryCooldownSec, setLimitRetryCooldownSec] = useState('300');
  const [limitRecoverySlippage, setLimitRecoverySlippage] = useState('1.0');
  const [limitRecoveryMaxLossPct, setLimitRecoveryMaxLossPct] = useState('3.0');
  const [limitSaving, setLimitSaving] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);
  const [expandedLimitOrderId, setExpandedLimitOrderId] = useState<number | null>(null);
  const [limitAttempts, setLimitAttempts] = useState<Record<number, any[]>>({});
  const [limitConfigDrafts, setLimitConfigDrafts] = useState<Record<number, any>>({});

  useEffect(() => {
    setQuoteResult(null);
    setPreparedSwap(null);
    setQuoteError(null);
    setStatusMessage(null);
  }, [tokenIn, tokenOut, provider, amount, slippage, splits, pathLength]);

  useEffect(() => {
    if (expandedLimitOrderId === null) return;
    let cancelled = false;
    const load = async () => {
      try {
        const attempts = await api.getDexLimitOrderAttempts(expandedLimitOrderId);
        if (!cancelled) setLimitAttempts(prev => ({ ...prev, [expandedLimitOrderId]: attempts }));
      } catch {}
    };
    load();
    const timer = window.setInterval(load, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [expandedLimitOrderId]);

  const requestPayload = {
    tokenIn,
    tokenOut,
    amountIn: amount,
    provider,
    slippage: Number(slippage),
    maxSplits: Number(splits),
    maxPathLength: Number(pathLength),
  };

  const swapTokenSides = () => {
    setTokenIn(tokenOut);
    setTokenOut(tokenIn);
  };

  const simulate = async () => {
    setQuoteLoading(true);
    setQuoteError(null);
    setStatusMessage(null);

    try {
      setQuoteResult(await api.getDexQuote(requestPayload));
    } catch (e: any) {
      setQuoteResult(null);
      setQuoteError(e?.message || String(e));
    } finally {
      setQuoteLoading(false);
    }
  };

  const prepareSwap = async () => {
    setPrepareLoading(true);
    setQuoteError(null);
    setStatusMessage(null);

    try {
      const result = await api.prepareDexSwap(requestPayload);
      setPreparedSwap(result.swap);
      setQuoteResult({
        best: result.swap,
        quotes: result.swap ? [result.swap] : [],
        errors: result.errors || [],
      });
      refetchWalletStatus();
    } catch (e: any) {
      setPreparedSwap(null);
      setQuoteError(e?.message || String(e));
    } finally {
      setPrepareLoading(false);
    }
  };

  const sendSwap = async () => {
    if (!preparedSwap) return;
    if (!walletStatus?.canExecute) {
      setQuoteError('Отправка backend-кошельком сейчас заблокирована настройками безопасности.');
      return;
    }
    if (Math.floor(Date.now() / 1000) >= preparedSwap.validUntil - 10) {
      setPreparedSwap(null);
      setQuoteError('Котировка устарела. Подготовь swap заново.');
      return;
    }

    setSendLoading(true);
    setQuoteError(null);
    setStatusMessage(null);

    try {
      const result = await api.executeDexSwap({
        swapId: preparedSwap.swapId,
        confirmation: 'EXECUTE_MANAGED_WALLET_SWAP',
      });
      setStatusMessage(
        'Подписано и отправлено в TON. Signed message: ' + shortAddress(result.signedMessageHash)
      );
      setPreparedSwap(null);
    } catch (e: any) {
      setQuoteError(e?.message || 'Транзакция не была отправлена');
    } finally {
      setSendLoading(false);
      refetchWalletStatus();
    }
  };

  const walletBadgeClass = walletStatus?.canExecute
    ? 'badge-success'
    : walletStatus?.configured
      ? 'badge-warning'
      : 'badge-neutral';

  const createLimitOrder = async () => {
    setLimitSaving(true);
    setLimitError(null);
    try {
      await api.createDexLimitOrder({
        tokenOut: limitToken,
        amountUsdt: limitAmountUsdt,
        limitPriceUsdt,
        slippage: Number(limitSlippage),
        provider: limitProvider,
        maxSplits: 2,
        maxPathLength: 3,
        protectionMode: limitProtectionMode,
        maxAttempts: Number(limitMaxAttempts),
        retryCooldownSec: Number(limitRetryCooldownSec),
        recoverySlippage: Number(limitRecoverySlippage),
        recoveryMaxLossPct: Number(limitRecoveryMaxLossPct),
      });
      setLimitPriceUsdt('');
      await refetchLimitOrders();
    } catch (e: any) {
      setLimitError(e?.message || 'Не удалось создать лимитный ордер');
    } finally {
      setLimitSaving(false);
    }
  };

  const updateLimitOrder = async (id: number, action: 'pause' | 'resume' | 'cancel') => {
    setLimitError(null);
    try {
      await api.updateDexLimitOrder(id, { action });
      await refetchLimitOrders();
    } catch (e: any) {
      setLimitError(e?.message || 'Не удалось изменить лимитный ордер');
    }
  };

  const toggleLimitOrderHistory = async (order: any) => {
    const nextId = expandedLimitOrderId === order.id ? null : order.id;
    setExpandedLimitOrderId(nextId);
    if (!nextId) return;
    setLimitConfigDrafts(prev => ({
      ...prev,
      [order.id]: prev[order.id] || {
        protectionMode: order.protectionMode,
        maxAttempts: order.maxAttempts,
        retryCooldownSec: order.retryCooldownSec,
        recoverySlippage: order.recoverySlippage,
        recoveryMaxLossPct: order.recoveryMaxLossPct,
      },
    }));
    try {
      const attempts = await api.getDexLimitOrderAttempts(order.id);
      setLimitAttempts(prev => ({ ...prev, [order.id]: attempts }));
    } catch (e: any) {
      setLimitError(e?.message || 'Не удалось загрузить историю попыток');
    }
  };

  const updateLimitConfigDraft = (id: number, patch: Record<string, unknown>) => {
    setLimitConfigDrafts(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const saveLimitProtection = async (order: any) => {
    const draft = limitConfigDrafts[order.id] || order;
    setLimitError(null);
    try {
      await api.updateDexLimitOrder(order.id, {
        action: 'configure',
        protectionMode: draft.protectionMode,
        maxAttempts: Number(draft.maxAttempts),
        retryCooldownSec: Number(draft.retryCooldownSec),
        recoverySlippage: Number(draft.recoverySlippage),
        recoveryMaxLossPct: Number(draft.recoveryMaxLossPct),
      });
      await refetchLimitOrders();
    } catch (e: any) {
      setLimitError(e?.message || 'Не удалось сохранить защиту');
    }
  };

  const formatPrice = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return '-';
    const numberValue = Number(value);
    const decimals = numberValue >= 1 ? 6 : numberValue >= 0.01 ? 8 : 10;
    return truncateDecimalAmount(value, decimals);
  };

  const limitStatusLabel = (status: string) => ({
    active: 'активен',
    paused: 'пауза',
    triggering: 'отправляется',
    confirming: 'подтверждается',
    filled: 'исполнен',
    refunded: 'возврат USDT',
    intermediate_returned: 'промежуточный актив',
    recovering: 'восстановление',
    recovered: 'восстановлен',
    retry_wait: 'ждёт повтор',
    attention: 'внимание',
    cancelled: 'отменён',
    failed: 'ошибка',
  }[status] || status);

  const limitStatusBadge = (status: string) => {
    if (status === 'filled' || status === 'recovered' || status === 'refunded') return 'badge-success';
    if (['triggering', 'confirming', 'recovering', 'retry_wait', 'paused', 'intermediate_returned'].includes(status)) return 'badge-warning';
    if (status === 'failed' || status === 'cancelled' || status === 'attention') return 'badge-danger';
    return 'badge-neutral';
  };

  const protectionLabel = (mode: string) => ({
    stop: 'Остановиться',
    recover_pause: 'Вернуть и остановиться',
    recover_retry: 'Восстановить и повторить',
  }[mode] || mode);

  const attemptKindLabel = (kind: string) => ({
    original: 'Покупка',
    continue: 'Продолжение',
    unwind: 'Возврат в USDT',
  }[kind] || kind);

  return (
    <div>
      <div className="flex justify-between items-center gap-2 mb-2">
        <div>
          <div className="section-title">DEX Swap</div>
          <div className="text-xs text-muted">DeDust Router v2 и Omniston, подпись настроенным backend-кошельком</div>
        </div>
        <span className={'badge ' + walletBadgeClass}>
          {walletStatus?.address
            ? shortAddress(walletStatus.address) + (walletStatus.version ? ' · ' + walletStatus.version.toUpperCase() : '')
            : 'Кошелёк не настроен'}
        </span>
      </div>

      {walletStatus && !walletStatus.canExecute && (
        <div className="card swap-notice">
          {walletStatus.issue
            ? walletStatus.issue
            : walletStatus.killSwitch
              ? 'Отправка отключена: включён Kill Switch.'
              : walletStatus.settlementBlocked
                ? 'Обычные swaps заблокированы незавершённой сделкой. Доступно только её ручное восстановление.'
              : 'Подготовка доступна, отправка отключена. Задай TON_ROUTER_EXECUTION_ENABLED=confirmed и перезапусти backend.'}
        </div>
      )}

      <ManualTradeRecovery canExecute={!!walletStatus?.configured && !!walletStatus?.executionEnabled && !walletStatus?.killSwitch} onChange={refetchWalletStatus} />

      <div className="card">
        <div className="route-details-grid">
          <div className="route-field">
            <label>Провайдер</label>
            <select value={provider} onChange={e => setProvider(e.target.value)}>
              <option value="best">Лучший output</option>
              <option value="dedust">DeDust Router v2</option>
              <option value="omniston">Omniston (STON.fi)</option>
            </select>
          </div>
          <div className="route-field">
            <label>Отдаю</label>
            <select value={tokenIn} onChange={e => setTokenIn(e.target.value)}>
              {TOKENS.filter(token => token.value !== tokenOut).map(token => (
                <option key={token.value} value={token.value}>{token.label}</option>
              ))}
            </select>
          </div>
          <div className="route-field">
            <label>Получаю</label>
            <select value={tokenOut} onChange={e => setTokenOut(e.target.value)}>
              {TOKENS.filter(token => token.value !== tokenIn).map(token => (
                <option key={token.value} value={token.value}>{token.label}</option>
              ))}
            </select>
          </div>
          <div className="route-field">
            <label>Сумма {tokenIn}</label>
            <input type="number" min="0" step="any" value={amount} onChange={e => setAmount(e.target.value)} />
          </div>
          <div className="route-field">
            <label>Проскальзывание %</label>
            <input type="number" min="0" max="10" step="0.1" value={slippage} onChange={e => setSlippage(e.target.value)} />
          </div>
          <div className="route-field">
            <label>Макс. веток</label>
            <input type="number" min="1" max="4" value={splits} onChange={e => setSplits(e.target.value)} />
          </div>
          <div className="route-field">
            <label>Макс. длина пути</label>
            <input type="number" min="1" max="3" value={pathLength} onChange={e => setPathLength(e.target.value)} />
          </div>
        </div>

        <div className="flex gap-2 items-center mt-2 swap-actions">
          <button className="btn btn-outline" type="button" onClick={swapTokenSides}>Поменять местами</button>
          <button className="btn btn-outline" type="button" onClick={simulate} disabled={quoteLoading || prepareLoading}>
            {quoteLoading ? 'Запрашиваю...' : 'Котировка'}
          </button>
          <button
            className="btn btn-primary"
            type="button"
            onClick={prepareSwap}
            disabled={quoteLoading || prepareLoading || walletStatus?.configured === false}
          >
            {prepareLoading ? 'Готовлю транзакцию...' : 'Подготовить swap'}
          </button>
        </div>
      </div>

      <div className="section-title mt-2">Лимитная покупка на DEX</div>
      <div className="card">
        <div className="route-details-grid">
          <div className="route-field">
            <label>Купить</label>
            <select value={limitToken} onChange={e => setLimitToken(e.target.value)}>
              {TOKENS.filter(token => token.value !== 'USDT').map(token => (
                <option key={token.value} value={token.value}>{token.label}</option>
              ))}
            </select>
          </div>
          <div className="route-field">
            <label>Потратить USDT</label>
            <input type="number" min="0" step="any" value={limitAmountUsdt} onChange={e => setLimitAmountUsdt(e.target.value)} />
          </div>
          <div className="route-field">
            <label>Макс. цена за монету, USDT</label>
            <input type="number" min="0" step="any" value={limitPriceUsdt} onChange={e => setLimitPriceUsdt(e.target.value)} />
          </div>
          <div className="route-field">
            <label>Slippage %</label>
            <input type="number" min="0" max="10" step="0.1" value={limitSlippage} onChange={e => setLimitSlippage(e.target.value)} />
          </div>
          <div className="route-field">
            <label>DEX роутер</label>
            <select value={limitProvider} onChange={e => setLimitProvider(e.target.value)}>
              <option value="best">Авто, лучший</option>
              <option value="dedust">DeDust Router v2</option>
              <option value="omniston">Omniston</option>
            </select>
          </div>
          <div className="route-field">
            <label>Защита</label>
            <select value={limitProtectionMode} onChange={e => setLimitProtectionMode(e.target.value)}>
              <option value="stop">Остановиться</option>
              <option value="recover_pause">Вернуть в USDT и пауза</option>
              <option value="recover_retry">Восстановить и повторить</option>
            </select>
          </div>
          <div className="route-field">
            <label>Макс. попыток</label>
            <input type="number" min="1" max="20" step="1" value={limitMaxAttempts} onChange={e => setLimitMaxAttempts(e.target.value)} />
          </div>
          <div className="route-field">
            <label>Cooldown повтора, сек</label>
            <input type="number" min="10" max="86400" step="10" value={limitRetryCooldownSec} onChange={e => setLimitRetryCooldownSec(e.target.value)} />
          </div>
          <div className="route-field">
            <label>Slippage восстановления %</label>
            <input type="number" min="0" max="10" step="0.1" value={limitRecoverySlippage} onChange={e => setLimitRecoverySlippage(e.target.value)} />
          </div>
          <div className="route-field">
            <label>Макс. убыток восстановления %</label>
            <input type="number" min="0" max="100" step="0.1" value={limitRecoveryMaxLossPct} onChange={e => setLimitRecoveryMaxLossPct(e.target.value)} />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={createLimitOrder}
            disabled={limitSaving || !limitPriceUsdt || !walletStatus?.canExecute}
          >
            {limitSaving ? 'Создаю...' : 'Поставить лимитный ордер'}
          </button>
          <span className="text-xs text-muted">Очередь: одна проверка каждые 2 сек · цена с учётом объёма · исполнение не хуже заданного лимита</span>
        </div>
        {limitError && <div className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{limitError}</div>}
      </div>

      <div className="card limit-orders-card">
        <div className="flex items-center justify-between mb-1">
          <span className="section-title" style={{ margin: 0 }}>Лимитные ордера</span>
          <span className="text-xs text-muted">активных: {(limitOrders || []).filter((order: any) => order.status === 'active').length}</span>
        </div>
        <div className="limit-orders-table-wrap">
          <table className="limit-orders-table">
            <thead>
              <tr>
                <th>ID</th><th>Покупка</th><th>Сумма</th><th>Лимит</th><th>Сейчас</th>
                <th>Ожидается</th><th>Роутер</th><th>Защита</th><th>Статус</th><th>Проверено</th><th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {(limitOrders || []).map((order: any) => (
                <React.Fragment key={order.id}>
                <tr>
                  <td className="text-mono">#{order.id}</td>
                  <td><strong>{order.tokenOut}</strong></td>
                  <td className="text-mono">{formatSwapAmount(order.amountUsdt)} USDT</td>
                  <td className="text-mono">≤ {formatPrice(order.limitPriceUsdt)}</td>
                  <td className="text-mono">{formatPrice(order.currentPriceUsdt)}</td>
                  <td className="text-mono">{order.expectedAmountOut ? formatSwapAmount(order.expectedAmountOut) + ' ' + order.tokenOut : '-'}</td>
                  <td>{order.providerUsed || order.provider}</td>
                  <td>{protectionLabel(order.protectionMode)}</td>
                  <td>
                    <span className={'badge ' + limitStatusBadge(order.status)}>{limitStatusLabel(order.status)}</span>
                    {order.reason && <div className="limit-order-reason" title={order.reason}>{order.reason}</div>}
                  </td>
                  <td className="text-xs text-muted">{order.lastCheckedAt ? new Date(order.lastCheckedAt).toLocaleTimeString() : '-'}</td>
                  <td>
                    <div className="flex gap-1 limit-order-actions">
                      {order.status === 'active' && <button className="btn btn-sm btn-outline" onClick={() => updateLimitOrder(order.id, 'pause')}>Пауза</button>}
                      {['paused', 'failed', 'attention', 'refunded', 'recovered'].includes(order.status) && <button className="btn btn-sm btn-outline" onClick={() => updateLimitOrder(order.id, 'resume')}>Запустить</button>}
                      {!['triggering', 'confirming', 'recovering', 'filled', 'cancelled'].includes(order.status) && <button className="btn btn-sm btn-outline" onClick={() => updateLimitOrder(order.id, 'cancel')}>Отменить</button>}
                      <button className="btn btn-sm btn-outline" onClick={() => toggleLimitOrderHistory(order)}>
                        {expandedLimitOrderId === order.id ? 'Скрыть' : 'История'}
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedLimitOrderId === order.id && (() => {
                  const draft = limitConfigDrafts[order.id] || order;
                  const attempts = limitAttempts[order.id] || [];
                  return (
                    <tr className="limit-order-detail-row">
                      <td colSpan={11}>
                        <div className="limit-order-protection-grid">
                          <div className="route-field">
                            <label>Режим защиты</label>
                            <select value={draft.protectionMode} onChange={e => updateLimitConfigDraft(order.id, { protectionMode: e.target.value })}>
                              <option value="stop">Остановиться</option>
                              <option value="recover_pause">Вернуть в USDT и пауза</option>
                              <option value="recover_retry">Восстановить и повторить</option>
                            </select>
                          </div>
                          <div className="route-field">
                            <label>Макс. попыток</label>
                            <input type="number" min="1" max="20" value={draft.maxAttempts} onChange={e => updateLimitConfigDraft(order.id, { maxAttempts: e.target.value })} />
                          </div>
                          <div className="route-field">
                            <label>Cooldown, сек</label>
                            <input type="number" min="10" max="86400" value={draft.retryCooldownSec} onChange={e => updateLimitConfigDraft(order.id, { retryCooldownSec: e.target.value })} />
                          </div>
                          <div className="route-field">
                            <label>Recovery slippage %</label>
                            <input type="number" min="0" max="10" step="0.1" value={draft.recoverySlippage} onChange={e => updateLimitConfigDraft(order.id, { recoverySlippage: e.target.value })} />
                          </div>
                          <div className="route-field">
                            <label>Макс. recovery loss %</label>
                            <input type="number" min="0" max="100" step="0.1" value={draft.recoveryMaxLossPct} onChange={e => updateLimitConfigDraft(order.id, { recoveryMaxLossPct: e.target.value })} />
                          </div>
                          <button className="btn btn-sm btn-primary" onClick={() => saveLimitProtection(order)}>Применить защиту</button>
                        </div>
                        <div className="limit-order-flow-summary">
                          {order.intermediateToken && order.intermediateAmount && <span>Промежуточный: <b>{formatSwapAmount(order.intermediateAmount)} {order.intermediateToken}</b></span>}
                          {order.recoveredUsdt && <span>Восстановлено: <b>{formatSwapAmount(order.recoveredUsdt)} USDT</b></span>}
                          {order.lossUsdt && <span>Убыток recovery: <b>{formatSwapAmount(order.lossUsdt)} USDT</b></span>}
                          {order.traceId && <span>Trace: <b>{shortAddress(order.traceId)}</b></span>}
                        </div>
                        <table className="limit-attempts-table">
                          <thead>
                            <tr><th>№</th><th>Этап</th><th>Статус</th><th>Маршрут</th><th>Ожидали / минимум</th><th>Факт</th><th>TX / trace</th><th>Причина</th><th>Когда</th></tr>
                          </thead>
                          <tbody>
                            {attempts.map((attempt: any) => (
                              <tr key={attempt.id}>
                                <td>{attempt.attemptNo}</td>
                                <td>{attemptKindLabel(attempt.kind)}</td>
                                <td><span className={'badge ' + limitStatusBadge(attempt.status)}>{limitStatusLabel(attempt.status)}</span></td>
                                <td className="text-mono">{formatSwapAmount(attempt.amountIn)} {attempt.tokenIn} → {attempt.tokenOut}<br />{attempt.provider || '-'}</td>
                                <td className="text-mono">{attempt.expectedAmountOut ? formatSwapAmount(attempt.expectedAmountOut) : '-'} / {attempt.minAmountOut ? formatSwapAmount(attempt.minAmountOut) : '-'}</td>
                                <td className="text-mono">
                                  {attempt.actualOutputAmount ? formatSwapAmount(attempt.actualOutputAmount) + ' ' + attempt.actualOutputToken :
                                    attempt.intermediateAmount ? formatSwapAmount(attempt.intermediateAmount) + ' ' + attempt.intermediateToken :
                                      attempt.refundedAmount ? formatSwapAmount(attempt.refundedAmount) + ' ' + attempt.tokenIn : '-'}
                                  {attempt.lossUsdt && <><br />loss {formatSwapAmount(attempt.lossUsdt)} USDT</>}
                                </td>
                                <td className="text-mono">{attempt.signedMessageHash ? shortAddress(attempt.signedMessageHash) : '-'}<br />{attempt.traceId ? shortAddress(attempt.traceId) : '-'}</td>
                                <td className="limit-order-reason">{attempt.reason || '-'}</td>
                                <td className="text-xs text-muted">{new Date(attempt.createdAt).toLocaleString()}</td>
                              </tr>
                            ))}
                            {attempts.length === 0 && <tr><td colSpan={9} className="text-muted">Попыток пока нет</td></tr>}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  );
                })()}
                </React.Fragment>
              ))}
              {(!limitOrders || limitOrders.length === 0) && (
                <tr><td colSpan={11} className="text-xs text-muted">Лимитных ордеров пока нет</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {quoteError && (
        <div className="card">
          <span className="badge badge-danger">Ошибка</span>
          <div className="text-sm mt-1">{quoteError}</div>
        </div>
      )}

      {statusMessage && (
        <div className="card">
          <span className="badge badge-success">Отправлено</span>
          <div className="text-sm mt-1">{statusMessage}</div>
        </div>
      )}

      {quoteResult?.best && (
        <div className="card">
          <div className="flex justify-between items-center gap-2">
            <div>
              <div className="text-sm text-secondary">{preparedSwap ? 'Транзакция подготовлена' : 'Лучший ответ'}</div>
              <div className="text-mono">
                {formatSwapAmount(quoteResult.best.amountIn)} {tokenIn} {'->'} {formatSwapAmount(quoteResult.best.amountOut)} {tokenOut}
              </div>
            </div>
            <span className="badge badge-success">{quoteResult.best.providerLabel}</span>
          </div>
          <div className="text-xs text-muted mt-1">Маршрут: {routeText(quoteResult.best)}</div>
          {quoteResult.best.gasBudget && (
            <div className="text-xs text-muted">Gas budget: {nanoTon(quoteResult.best.gasBudget)} TON</div>
          )}
        </div>
      )}

      {preparedSwap && (
        <div className="card swap-confirm">
          <div className="section-title">Подтверждение swap</div>
          <div className="swap-summary">
            <div><span>Кошелёк</span><strong>{shortAddress(preparedSwap.walletAddress)}</strong></div>
            <div><span>Отдаёшь</span><strong>{formatSwapAmount(preparedSwap.amountIn)} {preparedSwap.tokenIn}</strong></div>
            <div><span>Ожидается</span><strong>{formatSwapAmount(preparedSwap.amountOut)} {preparedSwap.tokenOut}</strong></div>
            <div><span>Минимум</span><strong>{formatSwapAmount(preparedSwap.minAmountOut)} {preparedSwap.tokenOut}</strong></div>
            <div><span>Сообщений</span><strong>{preparedSwap.messageCount}</strong></div>
            <div><span>Действует до</span><strong>{new Date(preparedSwap.validUntil * 1000).toLocaleTimeString()}</strong></div>
          </div>
          <button
            className="btn btn-primary mt-2"
            type="button"
            onClick={sendSwap}
            disabled={sendLoading || !walletStatus?.canExecute}
          >
            {sendLoading ? 'Подписываю и отправляю...' : 'Подписать и отправить'}
          </button>
        </div>
      )}

      {quoteResult?.quotes?.length > 1 && (
        <>
          <div className="section-title mt-2">Ответы провайдеров</div>
          {quoteResult.quotes.map((quote: any) => (
            <div key={quote.provider} className="route-row">
              <span className="route-token">{quote.provider}</span>
              <span className="route-direction">{quote.providerLabel}</span>
              <span className="route-spread positive">{formatSwapAmount(quote.amountOut)}</span>
              <span className="route-info">{routeText(quote)}</span>
            </div>
          ))}
        </>
      )}

      {quoteResult?.errors?.length > 0 && (
        <>
          <div className="section-title mt-2">Ошибки провайдеров</div>
          {quoteResult.errors.map((error: any) => (
            <div key={error.provider} className="route-row">
              <span className="route-token">{error.provider}</span>
              <span className="route-info">{error.error}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
